import { useRef } from "react";
import { t } from "@/i18n-js/instance";
import { useCreateReusableBlock } from "@/react/hooks/api/useReusableBlocks";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { useToast } from "@circle-react-uikit/ToastV2";

interface SaveReusableBlockModalProps {
  block: BaseBlock;
}

const SaveReusableBlockModalComponent = ({
  block,
}: SaveReusableBlockModalProps) => {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const modal = useModal();
  const toast = useToast();
  const { mutate: createReusableBlock } = useCreateReusableBlock({
    onSuccess: () => {
      toast.success(t("settings.site.pages.builder.save_reusable_block.saved"));
      modal.remove();
    },
    onError: () => {
      toast.error(t("settings.site.pages.builder.save_reusable_block.error"));
    },
  });

  const handleSave = ({ name }: { name: string }) => {
    createReusableBlock({
      name,
      block_data: block,
    });
  };

  return (
    <Modal
      title={t("settings.site.pages.builder.save_reusable_block.title")}
      isOpen={modal.visible}
      onClose={modal.remove}
      initialFocus={nameInputRef}
    >
      <Modal.Overlay />
      <Modal.Content size="md" className="max-w-screen-md">
        <Modal.Header className="md:pb-5">
          <Modal.Title size="sm">
            {t("settings.site.pages.builder.save_reusable_block.title")}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={modal.remove} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="!py-0">
          <Form
            id="save-reusable-block-form"
            onSubmit={handleSave}
            defaultValues={{
              name: "",
            }}
            className="h-full pb-0.5"
            formClassName="flex flex-col h-full"
          >
            <Form.Item
              name="name"
              label={t("settings.site.pages.builder.save_reusable_block.input")}
              hideDescription
              hideBorder
              className="!p-0"
            >
              <Form.Input name="name" ref={nameInputRef} />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2">
          <Button variant="secondary" onClick={modal.remove}>
            {t("settings.site.pages.builder.save_reusable_block.cancel")}
          </Button>
          <Button
            variant="circle"
            type="submit"
            form="save-reusable-block-form"
          >
            {t("settings.site.pages.builder.save_reusable_block.save")}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const SaveReusableBlockModal = ModalManager.create(
  ({ block }: SaveReusableBlockModalProps) => (
    <SaveReusableBlockModalComponent block={block} />
  ),
);

export const useSaveReusableBlockModal = () => useModal(SaveReusableBlockModal);
